import {AsyncResultModal} from "../../components/AsyncResultModal/AsyncResultModal"
import useAsyncResultIdle from "../../hooks/useAsyncResultIdle"
import React, {useEffect, useState} from "react"
import {createEmailOutreachSettings, getOutreachSettings, verifyOutreachSettings} from "../../resources/outreach"
import {AsyncResultComponent} from "../../containers/AsyncResult/AsyncResult"
import {useAsyncResult} from "../../hooks/useAsyncResult"
import {
	DataTable,
	DataTableActionHeader,
	DataTableBody,
	DataTableCard,
	DataTableCell,
	DataTableHead,
	DataTableRow,
} from "../../components/DataTable/DataTable"
import Button from "../../components/Button/Button"

interface Props {
	close: () => void
	accessToken: string
	onSuccess: () => void
}

interface FieldsProps {
	name: string
	setName: (name: string) => void
	domain: string
	setDomain: (domain: string) => void
	senderEmail: string
	setSenderEmail: (senderEmail: string) => void
	senderName: string
	setSenderName: (senderName: string) => void
	fieldsDisabled?: boolean
}

function Fields({
	name,
	setName,
	domain,
	setDomain,
	senderEmail,
	setSenderEmail,
	senderName,
	setSenderName,
	fieldsDisabled,
}: FieldsProps) {
	return (
		<>
			<div className="control">
				<label className="label">Name</label>
				<input
					className="input"
					type="text"
					required
					maxLength={255}
					value={name}
					disabled={fieldsDisabled}
					onChange={(e) => setName(e.target.value)}
				/>
			</div>
			<div className="control">
				<label className="label">Domain</label>
				<input
					className="input"
					type="text"
					required
					pattern={"^.*\\..*$"}
					maxLength={255}
					value={domain}
					disabled={fieldsDisabled}
					onChange={(e) => setDomain(e.target.value)}
				/>
			</div>
			<div className="control">
				<label className="label">Sender Email</label>
				<input
					className="input"
					required
					type="email"
					value={senderEmail}
					disabled={fieldsDisabled}
					onChange={(e) => setSenderEmail(e.target.value)}
				/>
			</div>
			<div className="control">
				<label className="label">Sender Name</label>
				<input
					className="input"
					type="text"
					maxLength={255}
					value={senderName}
					disabled={fieldsDisabled}
					onChange={(e) => setSenderName(e.target.value)}
				/>
			</div>
		</>
	)
}

export function CreateOutreachSettingsModal({close, accessToken, onSuccess}: Props) {
	const [createState, create] = useAsyncResultIdle(createEmailOutreachSettings)
	const [name, setName] = useState<string>("")
	const [domain, setDomain] = useState<string>("")
	const [senderEmail, setSenderEmail] = useState<string>("")
	const [senderName, setSenderName] = useState<string>("")

	return (
		<AsyncResultModal
			title="Create Email Outreach Settings"
			close={close}
			state={createState}
			onSubmit={() =>
				create(accessToken, {
					name,
					domain,
					sender: {
						email: senderEmail,
						name: senderName,
					},
				})
			}
			buttonText={"Create"}
			buttonClassname={"button is-info"}
			errorToText={(err) => (err.errors ? err.errors[0].title : "An unexpected error has occurred")}
			successText={"Created"}
			onSuccess={onSuccess}
		>
			<Fields
				name={name}
				setName={setName}
				domain={domain}
				setDomain={setDomain}
				senderEmail={senderEmail}
				setSenderEmail={setSenderEmail}
				senderName={senderName}
				setSenderName={setSenderName}
			/>
		</AsyncResultModal>
	)
}

export function EditOutreachSettingsModal({close, accessToken, onSuccess, id}: Props & {id: string}) {
	const [createState, create] = useAsyncResultIdle(createEmailOutreachSettings)
	const getSettingsState = useAsyncResult(() => getOutreachSettings(accessToken, id))
	const [name, setName] = useState<string>("")
	const [domain, setDomain] = useState<string>("")
	const [senderEmail, setSenderEmail] = useState<string>("")
	const [senderName, setSenderName] = useState<string>("")

	useEffect(() => {
		if (getSettingsState.isOk()) {
			const settings = getSettingsState.value

			console.log(settings)

			if (settings.type === "emailOutreachSettings") {
				setName(settings.attributes.name)
				setDomain(settings.attributes.domain)
				setSenderEmail(settings.attributes.sender.email)
				setSenderName(settings.attributes.sender.name ?? "")
			}
		}
	}, [getSettingsState])

	return (
		<AsyncResultModal
			classname={"edit-outreach-modal"}
			title="Edit Email Outreach Settings"
			close={close}
			state={createState}
			onSubmit={() =>
				create(accessToken, {
					name,
					domain,
					sender: {
						email: senderEmail,
						name: senderName,
					},
				})
			}
			buttonText={"Update"}
			buttonDisabled={true}
			buttonClassname={"edit-outreach-submit-button button is-info"}
			errorToText={(err) => (err.errors ? err.errors[0].title : "An unexpected error has occurred")}
			successText={"Updated"}
			onSuccess={onSuccess}
		>
			<AsyncResultComponent asyncResult={getSettingsState}>
				{({value: settings}) => {
					return settings.type === "emailOutreachSettings" ? (
						<>
							<Fields
								name={name}
								setName={setName}
								domain={domain}
								setDomain={setDomain}
								senderEmail={senderEmail}
								setSenderEmail={setSenderEmail}
								senderName={senderName}
								setSenderName={setSenderName}
								fieldsDisabled={true}
							/>
							{settings.attributes.dnsRecords && (
								<DataTableCard className={"dns-records-table-card"}>
									<DataTableActionHeader title={"DNS Records"}></DataTableActionHeader>
									<DataTable>
										<DataTableHead>
											<DataTableRow>
												<th>Domain</th>
												<th>Type</th>
												<th>Record Name</th>
											</DataTableRow>
										</DataTableHead>

										<DataTableBody>
											{settings.attributes.dnsRecords.map((record) => (
												<DataTableRow key={record.key}>
													<DataTableCell>{record.value}</DataTableCell>
													<DataTableCell>{record.recordType}</DataTableCell>
													<DataTableCell>{record.key}</DataTableCell>
												</DataTableRow>
											))}
											{!settings.attributes.isVerified && (
												<DataTableRow>
													<DataTableCell colSpan={3}>
														<Button
															type={"button"}
															theme={"success"}
															onClick={async () => {
																await verifyOutreachSettings(accessToken, settings.id)
																onSuccess()
															}}
														>
															Verify
														</Button>
													</DataTableCell>
												</DataTableRow>
											)}
										</DataTableBody>
									</DataTable>
								</DataTableCard>
							)}
						</>
					) : (
						<></>
					)
				}}
			</AsyncResultComponent>
		</AsyncResultModal>
	)
}
